import { useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { QUIZ_BY_LESSON_ADMIN } from '../../../gql/queries/academyQuery'
import {
  CREATE_ANSWER_OPTION,
  CREATE_QUESTION,
  CREATE_QUIZ,
  DELETE_ANSWER_OPTION,
  DELETE_QUESTION,
  DELETE_QUIZ,
  UPDATE_ANSWER_OPTION,
  UPDATE_QUIZ,
} from '../../../gql/mutations/academyMutations'
import {
  ICreateAnswerOptionResponse,
  ICreateQuestionResponse,
  ICreateQuizResponse,
  IDeleteAnswerOptionResponse,
  IDeleteQuestionResponse,
  IDeleteQuizResponse,
  ILesson,
  IQuizByLessonResponse,
  IUpdateAnswerOptionResponse,
  IUpdateQuizResponse,
} from '../../../types/academy'
import { parseId } from '../../../helpers'

interface IQuizBuilderModalProps {
  isOpen: boolean
  onClose: () => void
  lesson: ILesson
  // refetch del curso padre (para actualizar hasQuiz en la lista de lecciones)
  refetchCourse: () => void
}

export const QuizBuilderModal: React.FC<IQuizBuilderModalProps> = ({
  isOpen,
  onClose,
  lesson,
  refetchCourse,
}) => {
  const [newQuestionText, setNewQuestionText] = useState('')
  const [newOptionText, setNewOptionText] = useState<Record<string, string>>({})
  const [passingScore, setPassingScore] = useState<number | null>(null)

  const { data, loading, refetch } = useQuery<IQuizByLessonResponse>(QUIZ_BY_LESSON_ADMIN, {
    variables: { lessonId: Number(lesson.id) },
    fetchPolicy: 'network-only',
    skip: !isOpen,
  })

  const quiz = data?.quizByLesson

  const [createQuiz, { loading: creatingQuiz }] = useMutation<ICreateQuizResponse>(CREATE_QUIZ)
  const [updateQuiz] = useMutation<IUpdateQuizResponse>(UPDATE_QUIZ)
  const [deleteQuiz] = useMutation<IDeleteQuizResponse>(DELETE_QUIZ)
  const [createQuestion, { loading: creatingQuestion }] =
    useMutation<ICreateQuestionResponse>(CREATE_QUESTION)
  const [deleteQuestion] = useMutation<IDeleteQuestionResponse>(DELETE_QUESTION)
  const [createAnswerOption] = useMutation<ICreateAnswerOptionResponse>(CREATE_ANSWER_OPTION)
  const [updateAnswerOption] = useMutation<IUpdateAnswerOptionResponse>(UPDATE_ANSWER_OPTION)
  const [deleteAnswerOption] = useMutation<IDeleteAnswerOptionResponse>(DELETE_ANSWER_OPTION)

  const handleCreateQuiz = async () => {
    try {
      const result = await createQuiz({
        variables: {
          quizData: { lessonId: parseId(lesson.id), passingScorePercentage: 70 },
        },
      })
      if (result.data?.createQuiz.success) {
        toast.success('Quiz created — now add questions')
        refetch()
        refetchCourse()
      } else if (result.data?.createQuiz.errors?.length) {
        toast.error(`Error: ${result.data.createQuiz.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error creating quiz:', err)
      toast.error('Error creating quiz')
    }
  }

  const handleDeleteQuiz = async () => {
    if (!quiz) return
    if (!window.confirm('Delete this quiz with all its questions and attempts?')) return
    try {
      const result = await deleteQuiz({ variables: { id: parseId(quiz.id) } })
      if (result.data?.deleteQuiz.success) {
        toast.success('Quiz deleted')
        refetch()
        refetchCourse()
      } else if (result.data?.deleteQuiz.errors?.length) {
        toast.error(`Error: ${result.data.deleteQuiz.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error deleting quiz:', err)
      toast.error('Error deleting quiz')
    }
  }

  const handleSavePassingScore = async () => {
    if (!quiz || passingScore === null) return
    try {
      const result = await updateQuiz({
        variables: { id: parseId(quiz.id), passingScorePercentage: passingScore },
      })
      if (result.data?.updateQuiz.success) {
        toast.success('Passing score updated')
        setPassingScore(null)
        refetch()
      } else if (result.data?.updateQuiz.errors?.length) {
        toast.error(`Error: ${result.data.updateQuiz.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error updating quiz:', err)
      toast.error('Error updating quiz')
    }
  }

  const handleAddQuestion = async () => {
    if (!quiz || !newQuestionText.trim()) return
    try {
      const result = await createQuestion({
        variables: {
          questionData: { quizId: parseId(quiz.id), text: newQuestionText.trim() },
        },
      })
      if (result.data?.createQuestion.success) {
        setNewQuestionText('')
        refetch()
      } else if (result.data?.createQuestion.errors?.length) {
        toast.error(`Error: ${result.data.createQuestion.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error adding question:', err)
      toast.error('Error adding question')
    }
  }

  const handleDeleteQuestion = async (questionId: string) => {
    if (!window.confirm('Delete this question?')) return
    try {
      const result = await deleteQuestion({ variables: { id: parseId(questionId) } })
      if (result.data?.deleteQuestion.success) {
        refetch()
      } else if (result.data?.deleteQuestion.errors?.length) {
        toast.error(`Error: ${result.data.deleteQuestion.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error deleting question:', err)
      toast.error('Error deleting question')
    }
  }

  const handleAddOption = async (questionId: string) => {
    const text = (newOptionText[questionId] || '').trim()
    if (!text) return
    try {
      const result = await createAnswerOption({
        variables: {
          optionData: { questionId: parseId(questionId), text, isCorrect: false },
        },
      })
      if (result.data?.createAnswerOption.success) {
        setNewOptionText((prev) => ({ ...prev, [questionId]: '' }))
        refetch()
      } else if (result.data?.createAnswerOption.errors?.length) {
        toast.error(`Error: ${result.data.createAnswerOption.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error adding option:', err)
      toast.error('Error adding option')
    }
  }

  const handleToggleCorrect = async (optionId: string, isCorrect: boolean) => {
    try {
      const result = await updateAnswerOption({
        variables: { id: parseId(optionId), isCorrect },
      })
      if (result.data?.updateAnswerOption.success) {
        refetch()
      } else if (result.data?.updateAnswerOption.errors?.length) {
        toast.error(`Error: ${result.data.updateAnswerOption.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error updating option:', err)
      toast.error('Error updating option')
    }
  }

  const handleDeleteOption = async (optionId: string) => {
    try {
      const result = await deleteAnswerOption({ variables: { id: parseId(optionId) } })
      if (result.data?.deleteAnswerOption.success) {
        refetch()
      } else if (result.data?.deleteAnswerOption.errors?.length) {
        toast.error(`Error: ${result.data.deleteAnswerOption.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error deleting option:', err)
      toast.error('Error deleting option')
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-patch-question" /> Quiz Builder — {lesson.displayTitle}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : !quiz ? (
          <div className="text-center py-4">
            <i className="bi bi-patch-question" style={{ fontSize: 48, color: '#ccc' }} />
            <h5 className="mt-3 text-muted">This lesson has no quiz yet</h5>
            <p className="text-muted">Create a quiz to evaluate users after this lesson</p>
            <Button variant="primary" onClick={handleCreateQuiz} disabled={creatingQuiz}>
              {creatingQuiz ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <i className="bi bi-plus-circle me-1" />
              )}
              Create Quiz
            </Button>
          </div>
        ) : (
          <div>
            {/* Passing score */}
            <div className="d-flex align-items-end gap-2 mb-4 flex-wrap">
              <div>
                <Form.Label className="fw-bold mb-1">Passing score (%)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  style={{ width: 120 }}
                  value={passingScore ?? quiz.passingScorePercentage}
                  onChange={(e) => setPassingScore(Number(e.target.value))}
                />
              </div>
              {passingScore !== null && passingScore !== quiz.passingScorePercentage && (
                <Button size="sm" variant="outline-primary" onClick={handleSavePassingScore}>
                  <i className="bi bi-check-lg" /> Save
                </Button>
              )}
              <Button size="sm" variant="outline-danger" className="ms-auto" onClick={handleDeleteQuiz}>
                <i className="bi bi-trash me-1" /> Delete Quiz
              </Button>
            </div>

            {/* Questions */}
            <h6 className="mb-3">Questions ({quiz.questions?.length || 0})</h6>
            {(quiz.questions || []).map((question, idx) => (
              <div key={question.id} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center py-2">
                  <span className="fw-semibold">
                    {idx + 1}. {question.text}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteQuestion(question.id)}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </div>
                <div className="card-body py-2">
                  {question.options.map((option) => (
                    <div key={option.id} className="d-flex align-items-center gap-2 mb-1">
                      <Form.Check
                        type="checkbox"
                        id={`opt-correct-${option.id}`}
                        checked={Boolean(option.isCorrect)}
                        onChange={(e) => handleToggleCorrect(option.id, e.target.checked)}
                        title="Mark as correct answer"
                      />
                      <span
                        className={`flex-fill ${option.isCorrect ? 'text-success fw-semibold' : ''}`}
                      >
                        {option.text}
                        {option.isCorrect && <i className="bi bi-check-circle ms-1" />}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm p-0 border-0 text-danger"
                        onClick={() => handleDeleteOption(option.id)}
                      >
                        <i className="bi bi-x-lg" style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  ))}
                  {question.options.length === 0 && (
                    <div className="text-muted small mb-2">No options yet — add at least two</div>
                  )}
                  {!question.options.some((o) => o.isCorrect) && question.options.length > 0 && (
                    <div className="text-warning small mb-2">
                      <i className="bi bi-exclamation-triangle me-1" />
                      Mark at least one option as correct
                    </div>
                  )}

                  <div className="d-flex gap-2 mt-2">
                    <Form.Control
                      size="sm"
                      placeholder="New option text..."
                      value={newOptionText[question.id] || ''}
                      onChange={(e) =>
                        setNewOptionText((prev) => ({ ...prev, [question.id]: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddOption(question.id)
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleAddOption(question.id)}
                    >
                      <i className="bi bi-plus" /> Option
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {/* Add question */}
            <div className="d-flex gap-2 mt-3">
              <Form.Control
                placeholder="New question text..."
                value={newQuestionText}
                onChange={(e) => setNewQuestionText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddQuestion()
                  }
                }}
              />
              <Button
                variant="primary"
                onClick={handleAddQuestion}
                disabled={creatingQuestion || !newQuestionText.trim()}
              >
                <i className="bi bi-plus-circle me-1" /> Add Question
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
